import React, { useState } from 'react';
import { Card, Row, Col, DatePicker, Button, Statistic, Table, Space, Tag } from 'antd';
import { FileExcelOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useAuthStore from '../../store/useAuthStore';
import { getStockSummary } from '../../api/reports.api';
import { formatQty, formatDate } from '../../utils/formatters';
import { exportToCSV } from '../../utils/formatters';
import StockBadge from '../../components/common/StockBadge';

const StockSummaryPage = () => {
  const { user } = useAuthStore();
  const [asOfDate, setAsOfDate] = useState(dayjs());
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['stockSummary', user.plantID, asOfDate.format('YYYY-MM-DD')],
    queryFn: async () => {
      const res = await getStockSummary(user.plantID, { date: asOfDate.format('YYYY-MM-DD') });
      if (res && Array.isArray(res.items)) return res;
      if (Array.isArray(res)) return { items: res };
      return { items: [] };
    },
    enabled: !!user.plantID
  });

  const columns = [
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'HSN', dataIndex: 'hsnCode', key: 'hsnCode' },
    { title: 'Opening', dataIndex: 'openingQty', render: v => formatQty(v) },
    { title: 'Purchased', dataIndex: 'purchaseQty', render: v => formatQty(v) },
    { title: 'Consumed', dataIndex: 'consumptionQty', render: v => formatQty(v) },
    { 
      title: 'Balance', 
      dataIndex: 'balanceQty', 
      render: (v, record) => {
        const consumptionBalance = v * (record.mFactor || 1);
        return <b style={{ color: consumptionBalance <= 0 ? 'red' : consumptionBalance < 50 ? 'orange' : 'green' }}>{formatQty(consumptionBalance)}</b>;
      }
    },
    { title: 'Status', render: (_, r) => <StockBadge qty={r.balanceQty * (r.mFactor || 1)} /> }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card className="no-print">
        <Row gutter={16} align="middle">
          <Col>As of Date: <DatePicker value={asOfDate} onChange={setAsOfDate} /></Col>
          <Col><Button type="primary" icon={<SearchOutlined />} onClick={() => refetch()}>Load Report</Button></Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
              <Button icon={<FileExcelOutlined />} onClick={() => exportToCSV(data?.items || [], columns, 'StockSummary')}>Export CSV</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col span={6}><Card><Statistic title="Total Items" value={data?.totalItems} /></Card></Col>
        <Col span={6}><Card><Statistic title="Good Stock" value={data?.goodCount} styles={{ value: { color: 'green' } }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Low Stock" value={data?.lowCount} styles={{ value: { color: 'orange' } }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Critical" value={data?.criticalCount} styles={{ value: { color: 'red' } }} /></Card></Col>
      </Row>

      <Table dataSource={data?.items || []} columns={columns} loading={isLoading} pagination={false} rowKey="rawID" />
    </Space>
  );
};

export default StockSummaryPage;